import type { ConditionOp, FieldDef, IqMappingEntry, IqSource, IqSystemKey } from '@jfh/intake-shared';
import { CONDITION_OPS, CONDITION_OPS_NO_VALUE, resolveRepeaterSubFields } from '@jfh/intake-shared';
import { Icon } from '../../components/Icon.js';
import { IQ_SYSTEM_KEYS, type FieldGroup, type SourceKind } from './logic.js';

/** Edits one iQ mapping of any kind (the full editor behind "Edit"). */
export function MappingRow({
  entry,
  fields,
  fieldGroups,
  targetPlaceholder,
  onTargetChange,
  onKindChange,
  onSourceChange,
  onRemove,
}: {
  entry: IqMappingEntry;
  fields: FieldDef[];
  fieldGroups: FieldGroup[];
  targetPlaceholder: string;
  onTargetChange: (target: string) => void;
  onKindChange: (kind: SourceKind) => void;
  onSourceChange: (source: IqSource) => void;
  onRemove: () => void;
}) {
  const { source } = entry;
  const selectedField =
    source.type === 'field' ? fields.find((f) => f.id === source.fieldId) : undefined;
  const sourceIsAddress = selectedField?.type === 'address';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '12px 0',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          className="input mono"
          style={{ flex: 1, height: 32, fontSize: 12.5 }}
          placeholder={targetPlaceholder}
          value={entry.target}
          onChange={(e) => onTargetChange(e.target.value)}
        />
        <Icon name="chevronR" size={14} style={{ color: 'var(--text-3)' }} />
        <select
          className="select"
          style={{ width: 130, height: 32, fontSize: 12.5 }}
          value={source.type}
          onChange={(e) => onKindChange(e.target.value as SourceKind)}
        >
          <option value="field">Form field</option>
          <option value="const">Constant</option>
          <option value="contact">Primary contact</option>
          <option value="derived">Derived from field</option>
          <option value="lookup">From lookup (extra field)</option>
          <option value="password">Generated password</option>
          <option value="system">System value</option>
        </select>
        <button
          className="btn"
          aria-label="Remove mapping"
          style={{ height: 32, width: 32, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={onRemove}
        >
          <Icon name="trash" size={14} style={{ color: 'var(--text-3)' }} />
        </button>
      </div>

      {source.type === 'field' && (
        <>
          <select
            className="select"
            style={{ height: 32, fontSize: 12.5 }}
            value={source.fieldId}
            onChange={(e) => onSourceChange({ type: 'field', fieldId: e.target.value })}
          >
            {fields.length === 0 && <option value="">No fields available</option>}
            {fieldGroups.map((g) => (
              <optgroup key={g.title} label={g.title}>
                {g.fields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                    {f.type === 'address' ? ' — address' : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {sourceIsAddress && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              Maps as a nested address object —{' '}
              <code>{'{ AddressLine1… PostCode }'}</code>. Set the target to{' '}
              <code>Address</code> (or e.g. <code>RegisteredAddress</code>).
            </div>
          )}
        </>
      )}

      {source.type === 'const' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              className="input"
              style={{ flex: 1, height: 32, fontSize: 12.5 }}
              placeholder="Value"
              value={source.value}
              onChange={(e) => onSourceChange({ ...source, value: e.target.value })}
            />
            <select
              className="select"
              style={{ width: 110, height: 32, fontSize: 12.5 }}
              value={source.valueType ?? 'string'}
              onChange={(e) =>
                onSourceChange({ ...source, valueType: e.target.value as 'string' | 'number' | 'boolean' })
              }
            >
              <option value="string">Text</option>
              <option value="number">Number</option>
              <option value="boolean">Yes-No</option>
            </select>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            A fixed value sent to iQ — not shown on the form or card.
          </div>
        </div>
      )}

      {source.type === 'system' && (
        <select
          className="select"
          style={{ height: 32, fontSize: 12.5 }}
          value={source.key}
          onChange={(e) => onSourceChange({ type: 'system', key: e.target.value as IqSystemKey })}
        >
          {IQ_SYSTEM_KEYS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      )}

      {source.type === 'password' && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          A fresh, random password is generated when the account is approved and
          sent here (set the target to iQ's web-account password field). The same
          password is included in the approval email — alongside the customer's
          email address as their username. It is never stored. Map the email
          field to iQ's username field with a <strong>Form field</strong> source.
        </div>
      )}

      {source.type === 'lookup' &&
        (() => {
          const lookupFields = fields.filter((f) => f.optionsSource?.type === 'iq');
          if (lookupFields.length === 0) {
            return (
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                Add a dropdown/radio field whose options come from an iQ lookup, then map one of
                its extra fields here.
              </div>
            );
          }
          const lf = lookupFields.find((f) => f.id === source.fieldId) ?? lookupFields[0]!;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <select
                className="select"
                style={{ height: 32, fontSize: 12.5 }}
                value={lf.id}
                onChange={(e) => onSourceChange({ ...source, fieldId: e.target.value })}
              >
                {lookupFields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
              <input
                className="input"
                style={{ height: 32, fontSize: 12.5 }}
                placeholder="Extra field name (e.g. postingMap)"
                value={source.field}
                onChange={(e) => onSourceChange({ ...source, field: e.target.value })}
              />
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                Posts the chosen <strong>extra field</strong> of the option selected in that
                lookup field — e.g. the posting map linked to the selected currency. Define the
                extra field on the lookup in <strong>Settings → Lookup lists</strong> first.
              </div>
            </div>
          );
        })()}

      {source.type === 'contact' &&
        (() => {
          const contactFields = fields.filter((f) => f.type === 'contact');
          if (contactFields.length === 0) {
            return (
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                Add a <strong>Contact</strong> field to this form to map from the primary contact.
              </div>
            );
          }
          const cf = contactFields.find((f) => f.id === source.fieldId) ?? contactFields[0]!;
          const subs = resolveRepeaterSubFields(cf);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {contactFields.length > 1 && (
                <select
                  className="select"
                  style={{ height: 32, fontSize: 12.5 }}
                  value={cf.id}
                  onChange={(e) => onSourceChange({ ...source, fieldId: e.target.value })}
                >
                  {contactFields.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
              )}
              <select
                className="select"
                style={{ height: 32, fontSize: 12.5 }}
                value={source.subFieldId}
                onChange={(e) =>
                  onSourceChange({ type: 'contact', fieldId: cf.id, subFieldId: e.target.value })
                }
              >
                {subs.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                    {s.type === 'address' ? ' — address' : ''}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                Pulls this sub-field from the contact marked “Primary” (defaults to the first). An
                address sub-field is sent as iQ's nested{' '}
                <code>{'{ AddressLine1… PostCode }'}</code>.
              </div>
            </div>
          );
        })()}

      {source.type === 'derived' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <select
            className="select"
            style={{ height: 32, fontSize: 12.5 }}
            value={source.fieldId}
            onChange={(e) => onSourceChange({ ...source, fieldId: e.target.value })}
          >
            {fieldGroups.map((g) => (
              <optgroup key={g.title} label={g.title}>
                {g.fields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            When that field equals a value, send the mapped value — e.g. Currency{' '}
            <code>GBP</code> → Posting map <code>GBP</code>. The driver field can be a
            staff/Teams-card field; this target need not appear on the form.
          </div>
          {source.rules.map((r, ri) => {
            const op = r.op ?? 'eq';
            const noValue = CONDITION_OPS_NO_VALUE.includes(op);
            return (
              <div key={ri} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  className="select"
                  style={{ width: 120, height: 32, fontSize: 12, flex: 'none' }}
                  value={op}
                  onChange={(e) =>
                    onSourceChange({
                      ...source,
                      rules: source.rules.map((x, i) =>
                        i === ri ? { ...x, op: e.target.value as ConditionOp } : x,
                      ),
                    })
                  }
                >
                  {CONDITION_OPS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {!noValue && (
                  <input
                    className="input"
                    style={{ flex: 1, minWidth: 0, height: 32, fontSize: 12.5 }}
                    placeholder="value"
                    value={r.equals}
                    onChange={(e) =>
                      onSourceChange({
                        ...source,
                        rules: source.rules.map((x, i) => (i === ri ? { ...x, equals: e.target.value } : x)),
                      })
                    }
                  />
                )}
                <Icon name="chevronR" size={14} style={{ color: 'var(--text-3)', flex: 'none' }} />
                <input
                  className="input"
                  style={{ flex: 1, minWidth: 0, height: 32, fontSize: 12.5 }}
                  placeholder="send value"
                  value={r.value}
                  onChange={(e) =>
                    onSourceChange({
                      ...source,
                      rules: source.rules.map((x, i) => (i === ri ? { ...x, value: e.target.value } : x)),
                    })
                  }
                />
                <button
                  className="btn"
                  aria-label="Remove rule"
                  style={{ height: 32, width: 32, padding: 0, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  onClick={() =>
                    onSourceChange({ ...source, rules: source.rules.filter((_, i) => i !== ri) })
                  }
                >
                  <Icon name="trash" size={14} style={{ color: 'var(--text-3)' }} />
                </button>
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              onClick={() =>
                onSourceChange({ ...source, rules: [...source.rules, { equals: '', value: '' }] })
              }
            >
              <Icon name="plus" size={14} style={{ color: 'var(--accent)' }} /> Add rule
            </button>
          </div>
          <input
            className="input"
            style={{ height: 32, fontSize: 12.5 }}
            placeholder="Fallback value when no rule matches (optional)"
            value={source.fallback ?? ''}
            onChange={(e) => onSourceChange({ ...source, fallback: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}
